import { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import Loading from '@/components/Loading';
import useFetchCandidateEndpoint from '../api/useFetchCandidateEndpoint';
import { OFFER_STATUS_ITEMS } from '../constants';
import type { ScopeType } from '../api/useFetchCandidateEndpoint';
import type { JobPostEndpoint } from '../types/CandidateEndpoint';
import CandidatesOfferListItem from './CandidatesOfferListItem';
import CandidatesOfferListNotFound from './CandidatesOfferListNotFound';
import CandidatesJobPostEndpointsNewModal from './CandidatesJobPostEndpointsNewModal';
import CandidatesOfferSelectionBar from './CandidatesOfferSelectionBar';
import CandidatesOfferCopyModal from './CandidatesOfferCopyModal';
import AIRecommendationsPanel from '../ai_recommendations/components/AIRecommendationsPanel';
import { MdAdd } from 'react-icons/md';
import { LuSparkles } from 'react-icons/lu';
import usePostCandidateEndpointBulkProgress from '../api/usePostCandidateEndpointBulkProgress';
import usePostCandidateEndpointBulkWithdraw from '../api/usePostCandidateEndpointBulkWithdraw';
import usePostCandidateEndpointBulkReset from '../api/usePostCandidateEndpointBulkReset';
import usePostCandidateEndpointBulkDestroy from '../api/usePostCandidateEndpointBulkDestroy';
import useFetchIam from '../../api/useFetchIam';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';

type Props = {
  candidateId: number;
};

type ActionType = 'toSelection' | 'toWithdraw' | 'toSuggest' | 'toDestroy';

export default function CandidatesOfferCard({ candidateId }: Props) {
  const [selectedStatuses, setSelectedStatuses] = useState<Record<string, boolean>>({
    suggest: true,
    apply: true,
    decline: true,
    archived: true,
  });
  const [scope, setScope] = useState<Array<ScopeType> | undefined>(undefined);
  const [isCandidatesJobPostEndpointsOpen, setIsCandidatesJobPostEndpointsOpen] = useState(false);
  // AIでおすすめ求人を探すダイアログの開閉状態（中身は新経路: 適応型 AI求人検索 / OpenSearch関連度順）
  const [isAiJobSearchOpen, setIsAiJobSearchOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
  const {
    data: candidateEndpoint,
    refetch: candidateEndpointRefetch,
    isLoading: isCandidateEndpointLoading,
  } = useFetchCandidateEndpoint({
    id: candidateId,
    scopes: scope,
  });
  const isInitialEndpointLoading = isCandidateEndpointLoading && !candidateEndpoint;
  const isNoStatusSelected = Object.values(selectedStatuses).every((value) => !value);
  const jobPostEndpointCount = isNoStatusSelected
    ? 0
    : (candidateEndpoint?.job_post_endpoints.length ?? 0);
  const { data: iam } = useFetchIam();
  const isAiRecommendationEnabled = iam?.is_ai_matching_enabled ?? false;

  const { mutateAsync: mutateProgress } = usePostCandidateEndpointBulkProgress();
  const { mutateAsync: mutateWithdraw } = usePostCandidateEndpointBulkWithdraw();
  const { mutateAsync: mutateReset } = usePostCandidateEndpointBulkReset();
  const { mutateAsync: mutateDestroy } = usePostCandidateEndpointBulkDestroy();

  const handleStatusChange = (status: string) => {
    const newSelectedStatuses = {
      ...selectedStatuses,
      [status]: !selectedStatuses[status],
    };
    setSelectedStatuses(newSelectedStatuses);
  };

  useEffect(() => {
    const offerStatusValues = OFFER_STATUS_ITEMS.map((item) => item.value);
    const selectedOfferStatuses = Object.entries(selectedStatuses)
      .filter(([status, isSelected]) => isSelected && offerStatusValues.includes(status))
      .map(([status]) => status) as Array<ScopeType>;

    if (selectedOfferStatuses.length === 0) {
      // 何も選択されていない場合は空配列を指定して結果を0件にする
      setScope([]);
    } else if (selectedOfferStatuses.length === offerStatusValues.length) {
      // 全て選択されている場合はフィルタリングせず全件表示
      setScope(undefined);
    } else {
      setScope(selectedOfferStatuses);
    }
  }, [selectedStatuses]);

  // チェックボックスの選択/解除ハンドラー
  const handleCheckedChange = useCallback((id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  // 全選択解除のハンドラー
  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // 一括操作は「提案中/応募中」と「辞退/取り下げ」を跨いで選択できないため、
  // どちらか一方のグループだけに絞り込まれているときのみ全選択を出す
  const canSelectAll = useMemo(() => {
    const filteredStatuses = OFFER_STATUS_ITEMS.map((item) => item.value).filter(
      (value) => selectedStatuses[value]
    );
    if (filteredStatuses.length === 0) return false;

    const isActionableOnly = filteredStatuses.every(
      (status) => status === 'suggest' || status === 'apply'
    );
    const isResettableOnly = filteredStatuses.every(
      (status) => status === 'decline' || status === 'archived'
    );

    return isActionableOnly || isResettableOnly;
  }, [selectedStatuses]);

  const selectableIds = useMemo(() => {
    if (!candidateEndpoint?.job_post_endpoints) return [];
    return candidateEndpoint.job_post_endpoints
      .filter((endpoint) => endpoint.status !== 'matched')
      .map((endpoint) => endpoint.id);
  }, [candidateEndpoint]);

  const groupedEndpoints = useMemo(() => {
    const groups = new Map<string, JobPostEndpoint[]>();
    (candidateEndpoint?.job_post_endpoints ?? []).forEach((endpoint) => {
      const date = format(new Date(endpoint.created_at), 'yyyy/MM/dd');
      groups.set(date, [...(groups.get(date) ?? []), endpoint]);
    });
    return Array.from(groups, ([date, endpoints]) => ({ date, endpoints })).sort((a, b) =>
      b.date.localeCompare(a.date)
    );
  }, [candidateEndpoint]);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(selectableIds));
  }, [selectableIds]);

  // 一覧に表示されている求人だけを選択済みとして扱う（絞り込み・再取得で消えたものは無効化）
  const visibleSelectedEndpoints = useMemo(() => {
    if (!candidateEndpoint?.job_post_endpoints) return [];
    return candidateEndpoint.job_post_endpoints.filter((endpoint) => selectedIds.has(endpoint.id));
  }, [candidateEndpoint, selectedIds]);

  const selectedItemsStatuses = useMemo(
    () => visibleSelectedEndpoints.map((endpoint) => endpoint.status),
    [visibleSelectedEndpoints]
  );

  const selectedStatusGroup = useMemo(() => {
    if (selectedItemsStatuses.length === 0) return null;

    const hasActionableStatus = selectedItemsStatuses.some(
      (status) => status === 'suggest' || status === 'apply'
    );
    const hasResettableStatus = selectedItemsStatuses.some(
      (status) => status === 'decline' || status === 'archived'
    );

    if (hasActionableStatus) return 'actionable';
    if (hasResettableStatus) return 'resettable';
    return null;
  }, [selectedItemsStatuses]);

  // ボタンの表示制御
  const showToSuggest = useMemo(() => {
    return selectedItemsStatuses.some((status) => status === 'decline' || status === 'archived');
  }, [selectedItemsStatuses]);

  const showToWithdraw = useMemo(() => {
    return selectedItemsStatuses.some((status) => status === 'suggest' || status === 'apply');
  }, [selectedItemsStatuses]);

  const showToSelection = useMemo(() => {
    return selectedItemsStatuses.some((status) => status === 'suggest' || status === 'apply');
  }, [selectedItemsStatuses]);

  // 取り下げ済みが選択中に含まれる場合のみ削除ボタンを表示
  const showDelete = useMemo(() => {
    return selectedItemsStatuses.some((status) => status === 'archived');
  }, [selectedItemsStatuses]);

  const handleBulkAction = useCallback(
    async (action: ActionType) => {
      if (!candidateEndpoint?.job_post_endpoints || isBulkActionLoading) return;

      const filteredIds = visibleSelectedEndpoints
        .filter((endpoint) => {
          if (action === 'toSelection' || action === 'toWithdraw') {
            return endpoint.status === 'suggest' || endpoint.status === 'apply';
          } else if (action === 'toSuggest') {
            return endpoint.status === 'decline' || endpoint.status === 'archived';
          } else if (action === 'toDestroy') {
            // 物理削除は取り下げ済みのみ対象
            return endpoint.status === 'archived';
          }
          return false;
        })
        .map((endpoint) => endpoint.id);

      if (filteredIds.length === 0) return;

      setIsBulkActionLoading(true);
      try {
        if (action === 'toWithdraw') {
          await mutateWithdraw({ candidateProfileId: candidateId, ids: filteredIds });
        } else if (action === 'toSelection') {
          await mutateProgress({ candidateProfileId: candidateId, ids: filteredIds });
        } else if (action === 'toSuggest') {
          await mutateReset({ candidateProfileId: candidateId, ids: filteredIds });
        } else if (action === 'toDestroy') {
          await mutateDestroy({ candidateProfileId: candidateId, ids: filteredIds });
        }

        await candidateEndpointRefetch();
        showSuccessToast(action === 'toDestroy' ? '削除が完了しました' : '変更が完了しました');
        setSelectedIds(new Set());
      } catch (error) {
        showErrorToast(
          action === 'toDestroy' ? '削除に失敗しました' : 'ステータス変更に失敗しました'
        );
      } finally {
        setIsBulkActionLoading(false);
      }
    },
    [
      candidateEndpoint,
      visibleSelectedEndpoints,
      candidateId,
      isBulkActionLoading,
      mutateWithdraw,
      mutateProgress,
      mutateReset,
      mutateDestroy,
      candidateEndpointRefetch,
    ]
  );

  return (
    <div className="flex min-h-full flex-col">
      <div className="hidden-scrollbar flex max-h-[calc(100vh_+_-223px)] flex-1 flex-col overflow-y-auto px-lg pt-sm">
        <div className="flex items-center justify-between gap-md">
          <div className="flex items-center gap-xxl">
            <p className="text-heading-md">{jobPostEndpointCount}件</p>
            <div className="flex gap-lg">
              {OFFER_STATUS_ITEMS.map((i, key) => {
                return (
                  <Checkbox
                    key={key}
                    checked={selectedStatuses[i.value]}
                    onChange={() => handleStatusChange(i.value)}
                  >
                    {i.label}
                  </Checkbox>
                );
              })}
            </div>
          </div>
          <div className="flex shrink-0 gap-sm">
            {canSelectAll && selectableIds.length > 0 && (
              <Button color="tertiary" size="md" type="button" onClick={handleSelectAll}>
                全ての求人を選択
              </Button>
            )}
            {/* AIでおすすめ求人を探す（導線は従来ラベル、中身は新経路: 適応型 AI求人検索）（暫定的に有効組織のみ表示） */}
            {isAiRecommendationEnabled && (
              <Button
                color="secondary"
                size="md"
                type="button"
                onClick={() => setIsAiJobSearchOpen(true)}
              >
                <LuSparkles size={20} aria-hidden="true" />
                AIでおすすめ求人を探す
              </Button>
            )}
            <Button
              color="primary"
              size="md"
              type="button"
              onClick={() => setIsCandidatesJobPostEndpointsOpen(true)}
            >
              <MdAdd size={20} />
              提案求人を追加する
            </Button>
          </div>
        </div>
        <div className="w-full">
          <div className="mt-5 flex flex-col gap-md pb-xl">
            {isInitialEndpointLoading ? (
              <div className="mt-[69px] flex flex-col items-center justify-center gap-xs">
                <Loading size="small" />
                <p className="text-body-sm text-placeholder">提案求人を読み込み中です…</p>
              </div>
            ) : isNoStatusSelected || candidateEndpoint?.job_post_endpoints.length === 0 ? (
              <div className="mt-[69px] flex items-center justify-center">
                <CandidatesOfferListNotFound
                  CandidatesJobPostEndpointsOpen={() => setIsCandidatesJobPostEndpointsOpen(true)}
                />
              </div>
            ) : (
              groupedEndpoints.map(({ date, endpoints }) => (
                <section key={date} className="flex flex-col gap-xs">
                  <p className="text-heading-xs text-sub">{date}</p>
                  <div className="flex flex-col gap-md">
                    {endpoints.map((j) => (
                      <CandidatesOfferListItem
                        key={j.id}
                        jobPostEndpoint={j}
                        candidateProfileId={candidateId}
                        candidateEndpointRefetch={candidateEndpointRefetch}
                        isChecked={selectedIds.has(j.id)}
                        onCheckedChange={(checked) => handleCheckedChange(j.id, checked)}
                        selectedStatusGroup={selectedStatusGroup}
                      />
                    ))}
                  </div>
                </section>
              ))
            )}
          </div>
        </div>
        <CandidatesJobPostEndpointsNewModal
          open={isCandidatesJobPostEndpointsOpen}
          onOpenChange={setIsCandidatesJobPostEndpointsOpen}
          candidateProfileId={candidateId}
          onSuccess={candidateEndpointRefetch}
        />
      </div>
      <CandidatesOfferSelectionBar
        selectedCount={visibleSelectedEndpoints.length}
        onDeselectAll={handleDeselectAll}
        onCopy={() => setIsCopyModalOpen(true)}
        onToSuggest={() => handleBulkAction('toSuggest')}
        onToWithdraw={() => handleBulkAction('toWithdraw')}
        onToSelection={() => handleBulkAction('toSelection')}
        onDelete={() => handleBulkAction('toDestroy')}
        showToSuggest={showToSuggest}
        showToWithdraw={showToWithdraw}
        showToSelection={showToSelection}
        showDelete={showDelete}
        isLoading={isBulkActionLoading}
      />

      <CandidatesOfferCopyModal
        open={isCopyModalOpen}
        onOpenChange={setIsCopyModalOpen}
        candidateProfileId={candidateId}
        jobPostEndpointIds={visibleSelectedEndpoints.map((endpoint) => endpoint.id)}
        onSuccess={handleDeselectAll}
      />

      {/* AIでおすすめ求人を探すダイアログ（UIは旧Qdrant推薦のものを踏襲、中身は新しい適応型検索）
          candidateId が変わると key が変わりアンマウントされる。 */}
      <AIRecommendationsPanel
        key={`ai-job-search-${candidateId}`}
        candidateProfileId={candidateId}
        open={isAiJobSearchOpen}
        onOpenChange={setIsAiJobSearchOpen}
        onSuggested={candidateEndpointRefetch}
      />
    </div>
  );
}
